import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';
import { StoredFile } from './files';

export const ProductFileGroup = z
  .enum(['sliced', 'model', 'source', 'document'])
  .describe(
    'Fatiados (G-code, 3MF fatiado) · Modelos (STL, 3MF) · Fonte (STEP, F3D, Blender) · Documentos.',
  );
export type ProductFileGroup = z.infer<typeof ProductFileGroup>;
export const PRODUCT_FILE_GROUPS: { group: ProductFileGroup; label: string; empty: string }[] = [
  { group: 'sliced', label: 'Fatiados · G-code e 3MF fatiado', empty: 'Nenhum fatiado.' },
  { group: 'model', label: 'Modelos · STL e 3MF', empty: 'Nenhum modelo.' },
  { group: 'source', label: 'Fonte · STEP, F3D, Blender', empty: 'Nenhum arquivo-fonte.' },
  {
    group: 'document',
    label: 'Documentos',
    empty: 'Nenhum documento. Licenças, instruções e PDFs de NF entram aqui.',
  },
];

export const ProductFile = z
  .object({
    id: Uuid,
    group: ProductFileGroup,
    name: z.string(),
    lineage: z.string().describe('Same lineage = versions of the same file.'),
    version: z.number().int(),
    isCurrent: z.boolean(),
    changeNote: z.string().nullable(),
    variant: z.object({ id: Uuid, name: z.string() }).nullable(),
    usedBy: z
      .array(z.object({ id: Uuid, name: z.string() }))
      .describe('Variants whose print profile came from this version.'),
    file: StoredFile,
    thumbnailFileId: Uuid.nullable(),
    uploadedBy: z.string().nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ProductFile' });
export type ProductFile = z.infer<typeof ProductFile>;

export const ProductFileList = z.object({
  data: z.array(ProductFile).describe('Every version, newest first within each lineage.'),
  currentBytes: z.number().int().describe('Size of the current versions.'),
  totalBytes: z.number().int().describe('Size of all versions.'),
});

export const AttachProductFileBody = z
  .object({
    fileId: Uuid.describe('A file uploaded with `POST /files` or the resumable `/uploads`.'),
    group: ProductFileGroup,
    variantId: Uuid.nullable().default(null),
    changeNote: z.string().trim().max(300).nullable().default(null),
    onDuplicate: z
      .enum(['ask', 'copy'])
      .default('ask')
      .describe(
        '`ask`: 409 when an identical file (same sha256) is already in the product; `copy`: keep it as a copy.',
      ),
  })
  .meta({ id: 'AttachProductFileBody' });

const ProfileNumbers = z.object({
  grams: z.number(),
  printSeconds: z.number().int(),
  unitCostCentavos: z.number().int().nullable(),
});
export const ProfileDiff = z
  .object({
    variantId: Uuid,
    variantName: z.string(),
    productFileId: Uuid,
    before: ProfileNumbers.nullable(),
    after: ProfileNumbers,
  })
  .describe('What "Atualizar perfil" would change (per unit).');
export type ProfileDiff = z.infer<typeof ProfileDiff>;

export const AttachProductFileResult = z.object({
  file: ProductFile,
  profileDiffs: z.array(ProfileDiff),
});
export type AttachProductFileResult = z.infer<typeof AttachProductFileResult>;
